/**
 * Create the store
 */
import * as Sentry from '@sentry/react';
import localForage from 'localforage';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension';
import { createMigrate, persistReducer, persistStore } from 'redux-persist';
import createSagaMiddleware from 'redux-saga';

import rollbarMiddleware from './middleware/rollbar';
import migrations from './migrations';
import localUserSettingsMigrations from './modules/localUserSettings/migrations';
import localUserSettingsReducer from './modules/localUserSettings/reducer';
import createRootReducer from './reducers';
import rootSaga from './sagas';
import type { ApplicationRootState } from './types';

export const reduxPersistWhitelist: (keyof ApplicationRootState)[] = [
  'appScaffold',
  'assetTypes',
  'countries',
  'enterprise',
  'devices',
  'notifications',
  'samplePoints',
  'samplePointsStatistics',
  'sites',
  'states',
  'triggers',
  'users'
];

localForage.setDriver([localForage.INDEXEDDB, localForage.LOCALSTORAGE]);

const localUserSettingsPersistConfig = {
  key: 'localUserSettings',
  storage: localForage,
  version: 1,
  migrate: createMigrate(localUserSettingsMigrations, { debug: false }),
  blacklist: ['comparisonSettings'] // do not persist comparisonSettings upon page reloads
};

const reducerOverrides = {
  localUserSettings: persistReducer(localUserSettingsPersistConfig, localUserSettingsReducer)
};

const persistedReducer = () =>
  persistReducer(
    {
      version: 19,
      key: 'root',
      storage: localForage,
      debug: import.meta.env.VITE_ENV !== 'production',
      whitelist: reduxPersistWhitelist,
      migrate: createMigrate(migrations, { debug: false })
    },
    createRootReducer(reducerOverrides)
  );

const sagaMiddleware = createSagaMiddleware();

export default function configureStore() {
  // Create the store with two middleware
  // 1. sagaMiddleware: Makes redux-sagas work
  // 2. rollbarMiddleware: Logs exceptions to Rollbar
  const middlewares = [
    sagaMiddleware,
    rollbarMiddleware
  ];

  const enhancers = [
    applyMiddleware(...middlewares),
    Sentry.createReduxEnhancer()
  ];

  const store = createStore(
    persistedReducer(),
    composeWithDevTools(...enhancers)
  );

  sagaMiddleware.run(rootSaga);

  const persistor = persistStore(store);

  return { persistor, store };
}
